import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const ServiceItems = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [mappingFilter, setMappingFilter] = useState("");
  const [syncStates, setSyncStates] = useState({
    1: true,
    2: false,
    3: true,
  });

  // Summary cards data
  const summaryCardsData = [
    {
      id: 1,
      value: "42",
      label: "Total Services",
      icon: "bi:boxes",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
     
    },
    {
      id: 2,
      value: "38",
      label: "Synced Items",
      icon: "material-symbols:sync-outline",
     iconColor: "#DD992F",
      bgColor: "#FFF3E0",
    },
    {
      id: 3,
      value: "3",
      label: "Pending Items",
      icon: "material-symbols:pending-outline",
      iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
    {
      id: 4,
      value: "$78.50",
      label: "Avg.Rate",
      icon: "solar:dollar-linear",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
  ];

  // Service items data
  const serviceItems = [
    {
      id: 1,
      name: "Site Excavation",
      description: "Complete site excavation including grading and preparation",
      serviceCode: "SVC-EXC-001",
      qbName: "Site Excavation Services",
      unit: "Cubic Yard",
      lastSync: "2 hours ago",
      rate: "$45.00",
      billingUnit: "Cubic Yard",
      mapped: true,
      status: "synced",
      industryName: "Excavation",
    },
    {
      id: 2,
      name: "Electrical Installation",
      description: "Commercial and residential electrical installation",
      serviceCode: "SVC-ELEC-001",
      qbName: "Not mapped",
      unit: "Hour",
      lastSync: "Never",
      rate: "$85.00",
      billingUnit: "Hour",
      mapped: false,
      status: "not-mapped",
      industryName: "Electrical",
    },
    {
      id: 3,
      name: "Plumbing Services",
      description: "Complete plumbing installation and repair services",
      serviceCode: "SVC-PLU-001",
      qbName: "Plumbing Services",
      unit: "Hour",
      lastSync: "Failed 4 hours ago",
      rate: "$75.00",
      billingUnit: "Hour",
      mapped: true,
      status: "failed",
      industryName: "Plumbing",
    },
  ];

  // Status chip styling
  const getStatusConfig = (status) => {
    const statusConfig = {
      synced: {
        color: "#E8F5E9",
        textColor: "#23A26D",
        label: "Synced",
        icon: "heroicons:check-circle",
      },
      "not-mapped": {
        color: "#FFF3E0",
        textColor: "#DD992F",
        label: "Pending",
        icon: "heroicons:exclamation-triangle",
      },
      failed: {
        color: "#FFEBEE",
        textColor: "#D80027",
        label: "Error",
        icon: "heroicons:x-circle",
      },
    };

    return statusConfig[status] || statusConfig["not-mapped"];
  };

  const getStatusChip = (status) => {
    const config = getStatusConfig(status);

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          minWidth: 100,
        }}
      />
    );
  };

  const handleSyncToggle = (serviceId, checked) => {
    setSyncStates(prev => ({
      ...prev,
      [serviceId]: checked
    }));
  };

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Services"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Single Main Card containing everything */}
        <Card
          sx={{
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header and Sync Now Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 4,
              }}
            >
             
              <Button
                variant="contained"
                color="main"
                size="medium"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Sync Now
              </Button>
            </Box>

            {/* Summary Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {summaryCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 120,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Service Items List */}
            <Box sx={{ mb: 4 }}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  p: 3,
                }}
              >
                {/* Search and Filters */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Service Items
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage service items and their mapping to QuickBooks Services
                </Typography>
              </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CustomTextField
                      placeholder="Search services"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: "28px",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon icon="tabler:search" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={mappingFilter}
                        onChange={(e) => setMappingFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary">
                            All
                          </Typography>
                        </MenuItem>
                        <MenuItem value="mapped">Mapped</MenuItem>
                        <MenuItem value="not-mapped">Not Mapped</MenuItem>
                        <MenuItem value="failed">Sync Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Service Items List */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {serviceItems.map((service, index) => (
                    <Card
                      key={service.id}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "visible",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        {/* Service Header */}
                        <Box
                          sx={{
                            p: 3,
                            pb: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {service.name}
                              </Typography>
                             
                             
                            </Box>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 1 }}>
                            <Typography variant="body1" color="text.secondary">
                              {service.description}
                            </Typography>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,ml:4 }}>
                                <Box
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  px: 3,
                                  py: 0.5,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "background.paper",
                                }}
                              >
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: 'text.secondary',
                                    ml: 0
                                  }}
                                >
                                  {service.industryName}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Sync
                                </Typography>
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={syncStates[service.id]}
                                    onChange={(e) => handleSyncToggle(service.id, e.target.checked)}
                                  />
                                  <span className="slider round"></span>
                                </label>
                                
                              </Box>
                              </Box>
                          </Box>
                          <Box>
                            {getStatusChip(service.status)}
                          </Box>
                        </Box>

                        {/* Service Details Grid */}
                        <Box sx={{ p: 3, pt: 0 }}>
                          <Grid container spacing={3}>
                            {/* Left Column - Service Information */}
                            <Grid item xs={12} md={6}>
                              <Card
                                sx={{
                                  borderRadius: 2,
                                  border: "1px solid",
                                  borderColor: "#003A700D",
                                  backgroundColor: "#003A700D",
                                  p: 4,
                                  pt: 5,
                                  pb: 5,
                                  height: "100%",
                                }}
                              >
                                
                                {/* Service Information Table-like Layout */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {/* Service Code Row */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' ,pt:3}}>
                                    <Typography variant="body1" color="text.secondary">
                                      Service Code
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 530 }}>
                                      {service.serviceCode}
                                    </Typography>
                                  </Box>

                                  {/* QB Name Row */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                      QB Name
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                      fontWeight: 530,
                                    }}>
                                      {service.qbName}
                                    </Typography>
                                  </Box>

                                  {/* Unit Row */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                      Unit
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 530 }}>
                                      {service.unit}
                                    </Typography>
                                  </Box>

                                  {/* Last Sync Row */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                      Last Sync
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                      fontWeight: 530,
                                    }}>
                                      {service.lastSync}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Card>
                            </Grid>

                            {/* Right Column - Rate and Mapping */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
                                {/* Rate Card - Reduced Height */}
                                <Card
                                  sx={{
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "#97A4B10D",
                                    backgroundColor: "#97A4B10D",
                                    p: 4.5,
                                    minHeight: 'auto',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body1" color="text.secondary">
                                      Rate per {service.billingUnit}
                                    </Typography>
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        fontWeight: 600,
                                      }}
                                    >
                                      {service.rate}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                      Billing Unit
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 530 }}>
                                      {service.billingUnit}
                                    </Typography>
                                  </Box>
                                </Card>

                                {/* Mapping Card - Only show for second record */}
                                {service.id === 2 && (
                                  <Card
                                    sx={{
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: "#DD992F80",
                                      backgroundColor: "#DD992F0D",
                                      p: 4,
                                      flex: 1,
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                      Map to QuickBooks Service
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      Select existing QB service or create new
                                    </Typography>
                                    
                                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                                      <Select
                                        displayEmpty
                                        value=""
                                        sx={{
                                          backgroundColor: "background.paper",
                                          "& .MuiSelect-select": {
                                            py: 1,
                                            height: "33px",
                                            display: "flex",
                                            alignItems: "center",
                                          },
                                        }}
                                      >
                                        <MenuItem value="">
                                          <Typography variant="body2" color="text.secondary">
                                            Select
                                          </Typography>
                                        </MenuItem>
                                        <MenuItem value="site-excavation">Site Excavation Services</MenuItem>
                                        <MenuItem value="electrical">Electrical Installation Services</MenuItem>
                                        <MenuItem value="plumbing">Plumbing Services</MenuItem>
                                        <MenuItem value="create-new">Create New Service</MenuItem>
                                      </Select>
                                    </FormControl>

                                    {service.mapped && service.status !== 'failed' && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Icon
                                          icon="heroicons:check-circle"
                                          width={16}
                                          height={16}
                                          style={{ color: "#23A26D" }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                          Synced {service.lastSync}
                                        </Typography>
                                      </Box>
                                    )}

                                    {service.status === 'failed' && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Icon
                                          icon="heroicons:x-circle"
                                          width={16}
                                          height={16}
                                          style={{ color: "#D80027" }}
                                        />
                                        <Typography variant="body2" color="#D80027">
                                          {service.lastSync}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Card>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Card>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default ServiceItems;